import React, { useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../lib/store';
import { setEnrollProgressInfo } from '../../lib/store/crm';
import { parseFinancialQuestion } from '../../lib/store/crm/helpers';
import { getExperinceEnrollRecoveryPhase, isFirstEnrolmentStepOfExperience } from '../../lib/store/selectors';

import { Preloader } from '../../components';
import EnrollHeaderLite from '../../components/Enroll/EnrollHeaderLite/EnrollHeaderLite';
import { EnrollProgressBarInitialData } from '../../components/Enroll/ProgressBar/ProgressBar.constants';
import { useIsMobile } from '../../hooks';
import { EnrollProgressBarReducer } from '../../reducers/EnrollProgressBar/EnrollProgressBarReducer';
import {
  EnrollProgressBarBackQuestionAction,
  EnrollProgressBarInitialiseAction,
} from '../../reducers/EnrollProgressBar/EnrollProgressBarReducer.constants';

import EnrollContent from './EnrollContent/EnrollContent';

import styles from './Enroll.module.scss';

const Enroll = (): JSX.Element => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const crmState = useSelector((state: RootState) => state.crm);
  const sections = crmState.enroll.financialQuestionnaireSections;
  const { enrollProgressInfo } = crmState.enroll;
  const individual = useSelector((state: RootState) => state.crm.individualExtendedInfo);
  const [progressBar, progressBarDispatch] = useReducer(EnrollProgressBarReducer, EnrollProgressBarInitialData);

  const parsedProgressBar = useSelector(
    (state: RootState) => parseFinancialQuestion(
      state.crm,
      state.crm.enroll.financialQuestionnaireSections,
      progressBar.currentPhaseIndex,
      progressBar.currentStepIndex,
    ),
  );

  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [triggeredForceUpdate, setTriggeredForceUpdate] = useState<boolean>(false);
  const [stepAndPhase, setStepAndPhase] = useState({ phase: 1, step: 1 });

  const isComponentFullyInitialised = isInitialised && (progressBar.phases.length > 1);
  const parsedQuestion = parseFinancialQuestion(crmState, sections, 3, 1);
  const isNoExperienceSelected = parsedQuestion?.selectedOption > -1
    && parsedQuestion?.options.pop()?.id === parsedQuestion?.selectedOption;

  useEffect(() => {
    if (isNoExperienceSelected !== enrollProgressInfo.isNoExperienceWithStock) {
      dispatch(
        setEnrollProgressInfo({ ...enrollProgressInfo, isNoExperienceWithStock: isNoExperienceSelected }),
      );
    }
  }, [crmState]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!triggeredForceUpdate) {
      const currentStepIndex = getExperinceEnrollRecoveryPhase(individual, isNoExperienceSelected);

      if (currentStepIndex >= 0) {
        setStepAndPhase({
          phase: 3,
          step: parsedProgressBar?.selectedOption > -1 ? currentStepIndex : 0,
        });
      }

      setTriggeredForceUpdate(true);
    }
  }, [
    individual,
    parsedProgressBar,
    isInitialised,
    sections,
    isNoExperienceSelected,
    triggeredForceUpdate,
    enrollProgressInfo,
  ]);

  useEffect(() => {
    if (!isInitialised) {
      setIsInitialised(true);
    } else {
      progressBarDispatch({
        type: EnrollProgressBarInitialiseAction,
        payload: { sections, ...stepAndPhase },
      });
    }
  }, [dispatch, stepAndPhase, isInitialised, sections]);

  const handleBackBtnPress = (): void => {
    progressBarDispatch(
      {
        type: EnrollProgressBarBackQuestionAction,
        payload: { isNoExperienceWithStock: isNoExperienceSelected },
      },
    );
  };

  const contentHtml = (
    <>
      <EnrollHeaderLite
        handleBackBtnPress={handleBackBtnPress}
        showBackButton={!isFirstEnrolmentStepOfExperience(progressBar.currentPhaseIndex, progressBar.currentStepIndex)}
        visibleProgressBar
        progressBarData={progressBar}
        isParentInitialised={isComponentFullyInitialised}
      />
      {isComponentFullyInitialised ? (
        <div className={styles.wrapperEnrollContent}>
          <EnrollContent
            handleBackBtnPress={handleBackBtnPress}
            progressBar={progressBar}
            progressBarDispatch={progressBarDispatch}
          />
        </div>
      ) : (
        <div className={styles.preloader}>
          <Preloader owner="Enroll" />
        </div>
      )}
    </>
  );

  if (isMobile) {
    return (
      <div className={styles.enrollScreenMob}>
        {contentHtml}
      </div>
    );
  }

  return contentHtml;
};

export default Enroll;
